import type { ReactElement } from 'react';
import { useCallback, useContext } from 'react';
import { Home } from 'react-feather';
import styled from '@emotion/styled';
import { BrandInsightChatContexts, ChatContextType } from '@trinity/chat';
import {
  CapabilitiesContext,
  ChatContextSelectorVisibleContext,
  ChatHomeButtonVisibleContext,
  ChatViewType,
  ChatViewTypeContext,
  LinkContext,
} from '@trinity/chat-components/components/BrandInsights/context';
import {
  ActiveChatSessionContext,
  ChatLandingCardsVisibleContext,
} from '@trinity/chat-components/components/Chat/context';
import { FilterButton } from '@trinity/chat-components/components/Chat.Filters';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { ChatAppPlatformContext, useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useNavigate } from '@trinity/chat-components/containers/Chat/RoutingProvider';
import { GenAIExternalPlatform } from '@trinity/management-app-client';
import { useIsFullPageChat } from '../Chat/hooks/useIsFullPageChat';
import { useAgentResources } from './AgentResources.Container';
import { ChatTabs } from './Chat.Tabs';
import { ContextSubjectDisplay } from './ContextSubjectDisplay';
import { ChatHeaderPresentation } from './Header.Presentation';
import { SimpleContextSelector } from './SimpleContextSelector';
import { SourceLink } from './SourceLink';

type Props = {
  className?: string;
  showContextDisplayOverride?: boolean;
};

function ChatHeaderController({ className, showContextDisplayOverride }: Props): ReactElement {
  const link = useContext(LinkContext);
  const capabilities = useContext(CapabilitiesContext);
  const session = useContext(ActiveChatSessionContext);
  const features = useGroupFeatures();
  const navigate = useNavigate();
  const landingCardsVisible = useContext(ChatLandingCardsVisibleContext);
  const isFullPageChat = useIsFullPageChat();
  const viewMode = useContext(ChatViewTypeContext);
  const platform = useContext(ChatAppPlatformContext);
  const homeButtonVisible = useContext(ChatHomeButtonVisibleContext);
  const contextSelectorVisible = useContext(ChatContextSelectorVisibleContext);
  const agentResources = useAgentResources();
  const { isDigitalTwin, interactionMode } = useAvatarPanel();

  const handleHomeClick = useCallback(() => {
    navigate('/home');
  }, [navigate]);

  // Digital twin interaction modes
  const isDTTextVoice = isDigitalTwin && interactionMode !== 'video';
  const isDTVideo = isDigitalTwin && interactionMode === 'video';

  // Visibility toggles
  const isLoading = agentResources.isLoading && !link.initialized;

  const showTabs = viewMode === ChatViewType.Page;

  const showHomeButton =
    homeButtonVisible &&
    features.homeButton &&
    isFullPageChat &&
    platform === GenAIExternalPlatform.GenAIPlatform;

  const showContextSelector = !isDTVideo && !(features.chatContextSelector && landingCardsVisible);

  const showContextDisplay =
    typeof showContextDisplayOverride !== 'undefined' ? showContextDisplayOverride : !isDTVideo;

  const showFilterButton = (() => {
    if (session.context.type === ChatContextType.Agent) {
      if (BrandInsightChatContexts.includes(session.context.data.identifier)) {
        return true;
      }
      return agentResources.data.filters.length > 0 || agentResources.data.dataSources.length > 0;
    }
    return true;
  })();

  const showInlineEndAction = isDTTextVoice && capabilities.canViewExternalLinks;

  const showEndAction = !isDTTextVoice && !isDTVideo && capabilities.canViewExternalLinks;

  return (
    <ChatHeaderPresentation
      className={className}
      isLoading={isLoading}
      shrinkToContent={isDTTextVoice}
      navigation={
        showHomeButton && (
          <Tooltip title="Home">
            <StyledHomeButton onClick={handleHomeClick}>
              <StyledHomeIcon size={20} />
            </StyledHomeButton>
          </Tooltip>
        )
      }
      filterAction={showFilterButton && <FilterButton />}
      contextSelector={showContextSelector && <SimpleContextSelector disabled={!contextSelectorVisible} />}
      contextDisplay={showContextDisplay && <ContextSubjectDisplay />}
      inlineEndAction={showInlineEndAction && <SourceLink />}
      tabs={showTabs && <ChatTabs />}
      endAction={showEndAction && <SourceLink />}
    />
  );
}

export { ChatHeaderController };

const StyledHomeButton = styled.div(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: '6px',
  borderRadius: '4px',
  transition: 'background-color 0.2s ease',

  '&:hover': {
    backgroundColor: theme.palette.gray.light2,
  },
}));

const StyledHomeIcon = styled(Home)(({ theme }) => ({
  color: theme.palette.primary.main,
}));
